import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Heading,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { Key, Warning } from 'phosphor-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { obterBot, revogarToken } from '../api';
import { STATUS_META, TIPO_META } from '../constants';
import type { IManifesto } from '../types';

interface Props {
  botName: string | null;
  onClose: () => void;
  onGerarToken: (botName: string) => void;
}

const formatarData = (iso?: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR');
};

const ManifestoView: React.FC<{ manifesto: IManifesto }> = ({ manifesto }) => {
  const params = Object.entries(manifesto.params);

  return (
    <VStack align="stretch" spacing={4}>
      {manifesto.agenda && (
        <Box>
          <Heading size="xs" mb={2}>
            Agenda
          </Heading>
          <HStack spacing={3} mb={2}>
            <Code fontSize="sm">{manifesto.agenda.cron || 'sem cron'}</Code>
            <Badge colorScheme={manifesto.agenda.dono === 'plataforma' ? 'green' : 'gray'}>
              {manifesto.agenda.dono === 'plataforma' ? 'controlada aqui' : 'controlada no bot'}
            </Badge>
          </HStack>
          {manifesto.agenda.descricao && (
            <Text fontSize="sm" color="gray.600">
              {manifesto.agenda.descricao}
            </Text>
          )}
          {manifesto.agenda.dono === 'local' && (
            <Alert status="info" mt={2} borderRadius="md" fontSize="xs">
              <AlertIcon />
              Este horário é o que o bot declara. Mudar aqui <strong>não muda</strong> o agendamento
              na máquina dele — serve para sabermos quando ele deveria ter rodado.
            </Alert>
          )}
        </Box>
      )}

      <Box>
        <Heading size="xs" mb={2}>
          Parâmetros ({params.length})
        </Heading>
        {params.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            O bot não declarou parâmetros configuráveis.
          </Text>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Nome</Th>
                <Th>Tipo</Th>
                <Th>Padrão</Th>
              </Tr>
            </Thead>
            <Tbody>
              {params.map(([nome, p]) => (
                <Tr key={nome}>
                  <Td>
                    <Code fontSize="xs">{nome}</Code>
                    {p.obrigatorio && (
                      <Badge ml={2} colorScheme="red" fontSize="9px">
                        obrigatório
                      </Badge>
                    )}
                    {p.descricao && (
                      <Text fontSize="xs" color="gray.500">
                        {p.descricao}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Badge fontSize="9px">{p.tipo}</Badge>
                  </Td>
                  <Td>
                    <Code fontSize="xs">{JSON.stringify(p.default ?? null)}</Code>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {manifesto.triggers.length > 0 && (
        <Box>
          <Heading size="xs" mb={2}>
            Ações sob demanda
          </Heading>
          <HStack wrap="wrap" spacing={2}>
            {manifesto.triggers.map(t => (
              <Badge key={t} colorScheme="blue">
                {t}
              </Badge>
            ))}
          </HStack>
        </Box>
      )}

      {manifesto.recursos.length > 0 && (
        <Box>
          <Heading size="xs" mb={2}>
            Recursos disputados
          </Heading>
          <HStack wrap="wrap" spacing={2}>
            {manifesto.recursos.map(r => (
              <Code key={r} fontSize="xs">
                {r}
              </Code>
            ))}
          </HStack>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Dois bots no mesmo recurso não rodam ao mesmo tempo — o lock resolve.
          </Text>
        </Box>
      )}

      <HStack fontSize="xs" color="gray.500" spacing={4}>
        {manifesto.versao_bot && <Text>bot v{manifesto.versao_bot}</Text>}
        {manifesto.versao_sdk && <Text>SDK v{manifesto.versao_sdk}</Text>}
      </HStack>
    </VStack>
  );
};

export const BotDetalheDrawer: React.FC<Props> = ({ botName, onClose, onGerarToken }) => {
  const queryClient = useQueryClient();

  const { data: bot, isLoading } = useQuery({
    queryKey: ['automacao', 'bot', botName],
    queryFn: () => obterBot(botName as string),
    enabled: !!botName,
  });

  const revogar = useMutation({
    mutationFn: revogarToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automacao'] });
    },
  });

  const statusMeta = bot ? STATUS_META[bot.status] : null;

  return (
    <Drawer isOpen={!!botName} onClose={onClose} size="lg" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <VStack align="start" spacing={1}>
            <Text fontSize="lg">{bot?.apelido || botName}</Text>
            <HStack spacing={2}>
              <Code fontSize="xs">{botName}</Code>
              {bot && (
                <Badge colorScheme={TIPO_META[bot.tipo_bot].cor}>
                  {TIPO_META[bot.tipo_bot].label}
                </Badge>
              )}
              {statusMeta && <Badge colorScheme={statusMeta.cor}>{statusMeta.label}</Badge>}
            </HStack>
          </VStack>
        </DrawerHeader>

        <DrawerBody>
          {isLoading || !bot ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : (
            <VStack align="stretch" spacing={6} py={2}>
              {bot.descricao && (
                <Text fontSize="sm" color="gray.600">
                  {bot.descricao}
                </Text>
              )}

              {statusMeta?.proximoPasso && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box fontSize="sm">
                    <Text>{statusMeta.explicacao}</Text>
                    <Text fontWeight="semibold">Próximo passo: {statusMeta.proximoPasso}</Text>
                  </Box>
                </Alert>
              )}

              <Box>
                <Flex justify="space-between" align="center" mb={3}>
                  <Heading size="sm">Credenciais</Heading>
                  <Button
                    size="xs"
                    leftIcon={<Key size={14} />}
                    onClick={() => onGerarToken(bot.bot_name)}
                    colorScheme="blue"
                  >
                    Gerar token
                  </Button>
                </Flex>

                {bot.tokens.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    Nenhuma credencial. Sem token, o bot não consegue falar com a API.
                  </Text>
                ) : (
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Token</Th>
                        <Th>Último uso</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {bot.tokens.map(t => (
                        <Tr key={t.id} opacity={t.ativo ? 1 : 0.45}>
                          <Td>
                            <Code fontSize="xs">{t.prefixo}…</Code>
                            <Text fontSize="xs" color="gray.500">
                              {t.descricao || 'sem descrição'} · {t.criado_por || '—'}
                            </Text>
                            {!t.ativo && (
                              <Badge colorScheme="red" fontSize="9px">
                                revogado
                              </Badge>
                            )}
                          </Td>
                          <Td fontSize="xs">
                            {formatarData(t.ultimo_uso_em)}
                            {t.ultimo_uso_ip && <Text color="gray.500">{t.ultimo_uso_ip}</Text>}
                          </Td>
                          <Td>
                            {t.ativo && (
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                isLoading={revogar.isPending && revogar.variables === t.id}
                                onClick={() => revogar.mutate(t.id)}
                              >
                                Revogar
                              </Button>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}

                {bot.tokens.filter(t => t.ativo).length > 1 && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Duas credenciais ativas: normal durante rotação. Revogue a antiga depois de
                    confirmar que o bot subiu com a nova.
                  </Text>
                )}
              </Box>

              <Divider />

              <Box>
                <Heading size="sm" mb={3}>
                  Manifesto
                </Heading>
                {bot.manifesto ? (
                  <>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                      Declarado pelo bot em {formatarData(bot.manifesto_em)}
                    </Text>
                    <ManifestoView manifesto={bot.manifesto} />
                  </>
                ) : (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon as={Warning} />
                    <Text fontSize="sm">
                      O bot ainda não se apresentou. Enquanto isso não acontece, não há o que
                      oferecer de controle — é ele quem declara os parâmetros e ações que aceita.
                    </Text>
                  </Alert>
                )}
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
